import { supabase } from '@/lib/supabase';
import { Plan, PlanFormData } from '@/features/plans/types';
import { changeTrackingService } from '@/services/changeTracking';

export const planMutations = {
  createPlan: async (
    providerId: number,
    versionId: number,
    data: PlanFormData
  ): Promise<Plan> => {
    // If color_index is not set or is 0, assign the first available color
    if (!data.color_index || data.color_index === 0) {
      // Get existing plans to find used colors
      const { data: existingPlans, error: plansError } = await supabase
        .from('plans')
        .select('color_index')
        .eq('provider_id', providerId)
        .eq('version_id', versionId);

      if (plansError) throw plansError;

      // Get array of used color indices
      const usedColors = existingPlans.map(plan => plan.color_index);
      
      // Find the first available color index (1-30)
      let availableColor = 1;
      for (let i = 1; i <= 30; i++) {
        if (!usedColors.includes(i)) {
          availableColor = i;
          break;
        }
      }
      
      data.color_index = availableColor;
    }

    const { data: plan, error } = await supabase
      .from('plans')
      .insert([
        {
          provider_id: providerId,
          version_id: versionId,
          name: data.name,
          description: data.description,
          monthly_price: data.monthly_price,
          yearly_price: data.yearly_price,
          color_index: data.color_index,
          features: data.features || [],
          is_addon: data.is_addon
        }
      ])
      .select()
      .single();

    if (error) throw error;

    // Track plan creation
    try {
      // Get version data for region_id
      const { data: version } = await supabase
        .from('provider_versions')
        .select('region_id')
        .eq('id', versionId)
        .single();

      if (version) {
        await changeTrackingService.logEntityCreation({
          versionId,
          providerId,
          regionId: version.region_id,
          entityType: 'plans',
          entityId: plan.id,
          entityData: {
            id: plan.id,
            name: plan.name,
            description: plan.description,
            monthly_price: plan.monthly_price,
            yearly_price: plan.yearly_price,
            color_index: plan.color_index
          }
        });
      }
    } catch (trackingError) {
      console.error('Failed to track plan creation:', trackingError);
    }

    return {
      ...plan,
      channels: [],
      channel_count: 0
    } as unknown as Plan;
  },

  updatePlan: async (
    planId: number,
    data: Partial<PlanFormData>
  ): Promise<Plan> => {
    // Get old plan data for change tracking
    const { data: oldPlan } = await supabase
      .from('plans')
      .select(`
        *,
        provider_versions!version_id(region_id)
      `)
      .eq('id', planId)
      .single();

    const { data: plan, error } = await supabase
      .from('plans')
      .update(data)
      .eq('id', planId)
      .select(`
        *,
        channel_plans(count)
      `)
      .single();

    if (error) throw error;

    // Track plan field changes
    if (oldPlan) {
      try {
        const fieldsToTrack = ['name', 'description', 'monthly_price', 'yearly_price', 'color_index'];
        const changes = changeTrackingService.generateFieldChanges(
          oldPlan,
          { ...oldPlan, ...data },
          fieldsToTrack
        );

        if (changes.length > 0) {
          await changeTrackingService.logBulkChanges({
            versionId: oldPlan.version_id!,
            providerId: oldPlan.provider_id!,
            regionId: (oldPlan.provider_versions as any)[0]?.region_id,
            entityType: 'plans',
            entityId: planId,
            changes
          });
        }
      } catch (trackingError) {
        console.error('Failed to track plan update:', trackingError);
      }
    }

    return {
      ...plan,
      channels: [],
      channel_count: plan.channel_plans?.[0]?.count ?? 0
    } as unknown as Plan;
  },

  deletePlan: async (planId: number): Promise<void> => {
    // Get plan data before deletion for change tracking
    const { data: planToDelete } = await supabase
      .from('plans')
      .select(`
        *,
        provider_versions!version_id(region_id)
      `)
      .eq('id', planId)
      .single();

    const { error } = await supabase
      .from('plans')
      .delete()
      .eq('id', planId);

    if (error) throw error;

    // Track plan deletion
    if (planToDelete) {
      try {
        await changeTrackingService.logEntityDeletion({
          versionId: planToDelete.version_id!,
          providerId: planToDelete.provider_id!,
          regionId: (planToDelete.provider_versions as any)[0]?.region_id,
          entityType: 'plans',
          entityId: planId,
          entityData: {
            id: planToDelete.id,
            name: planToDelete.name,
            description: planToDelete.description,
            monthly_price: planToDelete.monthly_price,
            yearly_price: planToDelete.yearly_price,
            color_index: planToDelete.color_index
          }
        });
      } catch (trackingError) {
        console.error('Failed to track plan deletion:', trackingError);
      }
    }
  },

  assignChannels: async (
    planId: number,
    versionId: number,
    channelIds: number[]
  ): Promise<void> => {
    try {
      // Get plan and version data for tracking
      const [planResult, versionResult] = await Promise.all([
        supabase.from('plans').select('id, name, color_index, provider_id').eq('id', planId).single(),
        supabase.from('provider_versions').select('region_id').eq('id', versionId).single()
      ]);

      const { error } = await supabase
        .from('channel_plans')
        .insert(
          channelIds.map(channelId => ({
            plan_id: planId,
            channel_id: channelId,
            version_id: versionId
          }))
        );

      if (error) {
        console.error('Failed to assign channels:', error);
        throw new Error('Failed to create channel plan associations');
      }

      // Track plan assignments
      if (planResult.data && versionResult.data) {
        const plan = planResult.data;
        const version = versionResult.data;

        try {
          for (const channelId of channelIds) {
            await changeTrackingService.logPlanAssignment({
              versionId,
              providerId: plan.provider_id!,
              regionId: version.region_id,
              channelId,
              actionType: 'CREATE',
              newPlan: {
                id: plan.id,
                name: plan.name,
                color: plan.color_index || 1
              }
            });
          }
        } catch (trackingError) {
          console.error('Failed to track plan assignments:', trackingError);
        }
      }
    } catch (err) {
      console.error('Error in assignChannels:', err);
      throw new Error('Failed to create channel plan associations');
    }
  },

  removeChannels: async (
    planId: number,
    channelIds: number[]
  ): Promise<void> => {
    console.log('Removing channels:', { planId, channelIds });

    // Get plan and version data for tracking
    const { data: planData } = await supabase
      .from('plans')
      .select(`
        id, name, color_index, provider_id, version_id,
        provider_versions!version_id(region_id)
      `)
      .eq('id', planId)
      .single();

    const { error } = await supabase
      .from('channel_plans')
      .delete()
      .eq('plan_id', planId)
      .in('channel_id', channelIds);

    if (error) throw error;

    // Track plan removal
    if (planData) {
      try {
        for (const channelId of channelIds) {
          await changeTrackingService.logPlanAssignment({
            versionId: planData.version_id!,
            providerId: planData.provider_id!,
            regionId: (planData.provider_versions as any)[0]?.region_id,
            channelId,
            actionType: 'DELETE',
            oldPlan: {
              id: planData.id,
              name: planData.name,
              color: planData.color_index || 1
            }
          });
        }
      } catch (trackingError) {
        console.error('Failed to track plan removals:', trackingError);
      }
    }
  }
};
